import {gql} from '@apollo/client';
import React from 'react';

import {assert} from 'Asserts';
import * as FileConstants from 'folder-items/FileConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getRequiredLinkEntitySchemaFieldPath} from 'registry/field/FieldPathUtil';
import {
  ValidEntitiesForFieldPathQuery,
  ValidEntitiesForFieldPathQueryVariables,
} from 'schemas/tags/__generated/ValidEntitiesForFieldPathQuery';
import BioentityLinkInput from 'schemas/tags/BioentityLinkInput';
import {BaseBioentityLinkInputProps} from 'schemas/tags/BioentityLinkInputConstants';
import * as logging from 'util/Logging';

// Keep in sync with benchling/registry/graphql/graphql_resolvers.py
export const MAX_THRESHOLD_TO_USE_VALID_ENTITIES = 1000;

export const VALID_ENTITIES_FOR_FIELD_PATH_QUERY = gql`
  query ValidEntitiesForFieldPathQuery($bioentityId: StrictString!, $fieldId: StrictString!)
  @skipAudit
  @engComponent(name: SCHEMAS) {
    bioentity(id: $bioentityId) {
      ... on RegistryItemInterface {
        id
        validEntitiesForFieldPathInfo(fieldId: $fieldId) {
          numValidEntities
          validEntities {
            id
            name
            tagSchema {
              id
              iconId
            }
            folder {
              id
              source {
                ...SourceSummaryGraphQL
              }
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('SourceSummary').fragment}
`;

export type RequiredFieldPathAwareBioentityLinkInputProps =
  BaseBioentityLinkInputProps<FileConstants.SchemaTag> & {
    fileId: string | undefined;
    tag: FileConstants.SchemaTag;
  };

export default function RequiredFieldPathAwareBioentityLinkInput(
  props: RequiredFieldPathAwareBioentityLinkInputProps
): JSX.Element {
  // We only want to execute this GraphQL query if the tag's schema field has a required path. It's important
  // to skip the query otherwise, both for performance reasons and because it can cause a crash in some cases
  // (e.g. for entry schemas). Note that using the `skip` prop on BenchlingQuery is not sufficient to avoid
  // the latter problem.
  // We also skip querying if this is being rendered from the CreateBioentityModal. Since we don't have a
  // fileId yet, there's no way to compute valid entities.
  if (props.fileId == null || getRequiredLinkEntitySchemaFieldPath(props.tag.tagSchemaField) == null) {
    return <BioentityLinkInput {...props} loadingValidEntities={false} />;
  }
  return (
    <BenchlingQuery<ValidEntitiesForFieldPathQuery, ValidEntitiesForFieldPathQueryVariables>
      query={VALID_ENTITIES_FOR_FIELD_PATH_QUERY}
      variables={{bioentityId: props.fileId, fieldId: props.tag.tagSchemaField.apiId}}
      renderLoading={() => <BioentityLinkInput {...props} loadingValidEntities={true} />}
      renderCachedDataWhenRefetching={true}
      fetchPolicy="cache-and-network"
    >
      {(data) => {
        const bioentity = data.bioentity;
        assert(
          !isInaccessible(bioentity),
          'Expected bioentity for currently-rendered metadata tab to be accessible'
        );
        if (bioentity.validEntitiesForFieldPathInfo == null) {
          // This is very unlikely but can technically happen if the required path for this field was removed
          // at the exact right time
          return <BioentityLinkInput {...props} loadingValidEntities={false} />;
        }
        if (bioentity.validEntitiesForFieldPathInfo.numValidEntities > MAX_THRESHOLD_TO_USE_VALID_ENTITIES) {
          logging.info(
            `Using standard search dropdown for field with required path. num_valid_entities=${bioentity.validEntitiesForFieldPathInfo.numValidEntities}, field_id=${props.tag.tagSchemaField.apiId}`
          );
          return <BioentityLinkInput {...props} loadingValidEntities={false} />;
        }
        assert(
          bioentity.validEntitiesForFieldPathInfo.validEntities != null,
          'Expected non-null validEntities when numValidEntities is below max threshold'
        );
        return (
          <BioentityLinkInput
            {...props}
            loadingValidEntities={false}
            validEntities={bioentity.validEntitiesForFieldPathInfo.validEntities}
          />
        );
      }}
    </BenchlingQuery>
  );
}
